import { useEffect, useRef, useState } from "react";
import { C } from "../utils/tokens";

const safeR = (v) => Math.max(0.001, isNaN(v) ? 0.001 : Math.abs(v));
const lerp = (a, b, t) => a + (b - a) * t;

// ── 1. CANVAS BACKGROUND ──────────────────────────────────────────────────────
const CVSheetCanvas = ({ mouseRef }) => {
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let t = 0;
    const dpr = Math.min(window.devicePixelRatio, 2);

    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    const LINES = [
      { y: 0.18, w: 0.55, delay: 0.0, thick: 3.5, alpha: 0.9 },
      { y: 0.23, w: 0.35, delay: 0.8, thick: 1.8, alpha: 0.6 },
      { y: 0.32, w: 0.62, delay: 1.6, thick: 1.2, alpha: 0.45 },
      { y: 0.36, w: 0.58, delay: 2.0, thick: 1.2, alpha: 0.45 },
      { y: 0.4, w: 0.64, delay: 2.4, thick: 1.2, alpha: 0.45 },
      { y: 0.5, w: 0.28, delay: 3.2, thick: 2.0, alpha: 0.7 },
      { y: 0.55, w: 0.6, delay: 3.8, thick: 1.1, alpha: 0.4 },
      { y: 0.59, w: 0.56, delay: 4.2, thick: 1.1, alpha: 0.4 },
      { y: 0.63, w: 0.52, delay: 4.6, thick: 1.1, alpha: 0.4 },
      { y: 0.72, w: 0.25, delay: 5.2, thick: 2.0, alpha: 0.7 },
      { y: 0.77, w: 0.58, delay: 5.8, thick: 1.1, alpha: 0.4 },
      { y: 0.81, w: 0.5, delay: 6.2, thick: 1.1, alpha: 0.4 },
    ];
    const totalDuration = 8.5;
    const lineState = LINES.map(() => ({ progress: 0 }));

    const pts = Array.from({ length: 60 }, () => ({
      x: Math.random(),
      y: Math.random(),
      vx: (Math.random() - 0.5) * 0.00018,
      vy: (Math.random() - 0.5) * 0.00018,
      size: 0.6 + Math.random() * 1.4,
      phase: Math.random() * Math.PI * 2,
    }));

    const pen = { x: 0, y: 0, targetX: 0, targetY: 0, drawing: false };

    const draw = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      if (w < 1 || h < 1) {
        animId = requestAnimationFrame(draw);
        return;
      }

      ctx.fillStyle = "rgba(8,9,9,0.13)";
      ctx.fillRect(0, 0, w, h);

      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;
      const isMobile = w <= 768;

      const sheetW = isMobile
        ? Math.min(w * 0.75, 290)
        : Math.min(w * 0.38, 380);
      const sheetH = sheetW * 1.41;
      const sheetX = isMobile ? w * 0.5 - sheetW * 0.5 : w * 0.6;
      const sheetY = isMobile
        ? h * 0.46 - sheetH * 0.5
        : h * 0.5 - sheetH * 0.5;

      ctx.save();
      if (isMobile) ctx.globalAlpha = 0.15;

      ctx.shadowColor = "rgba(245,197,24,0.12)";
      ctx.shadowBlur = isMobile ? 30 : 60;
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      ctx.strokeStyle = "rgba(255,255,255,0.1)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.roundRect(sheetX, sheetY, sheetW, sheetH, 8);
      ctx.fill();
      ctx.stroke();
      ctx.shadowBlur = 0;

      const padX = sheetW * 0.1;
      const padY = sheetH * 0.08;
      const innerW = sheetW - padX * 2;
      const elapsed = ((t % totalDuration) / totalDuration) * totalDuration;

      let penSet = false;
      LINES.forEach((line, i) => {
        const progress = Math.min(1, Math.max(0, (elapsed - line.delay) / 0.7));
        lineState[i].progress = progress;

        const lx = sheetX + padX;
        const ly = sheetY + sheetH * line.y;
        const lw = innerW * line.w;

        if (progress > 0) {
          const drawnW = lw * progress;
          const grad = ctx.createLinearGradient(lx, 0, lx + drawnW, 0);
          grad.addColorStop(0, `rgba(245,197,24,${line.alpha})`);
          grad.addColorStop(0.7, `rgba(245,197,24,${line.alpha * 0.8})`);
          grad.addColorStop(1, `rgba(245,197,24,${line.alpha * 0.3})`);
          ctx.strokeStyle = grad;
          ctx.lineWidth = line.thick;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(lx, ly);
          ctx.lineTo(lx + drawnW, ly);
          ctx.stroke();

          if (progress < 1 && !penSet) {
            pen.targetX = lx + drawnW;
            pen.targetY = ly;
            pen.drawing = true;
            penSet = true;
          }
        }
      });
      if (!penSet) pen.drawing = false;

      pen.x = lerp(pen.x, pen.targetX, 0.12);
      pen.y = lerp(pen.y, pen.targetY, 0.12);

      if (pen.drawing) {
        ctx.save();
        ctx.translate(pen.x, pen.y);
        ctx.rotate(-0.5);
        const penLen = 28;
        const penW = 5;
        const penGrad = ctx.createLinearGradient(0, 0, penLen, 0);
        penGrad.addColorStop(0, "rgba(245,197,24,0.9)");
        penGrad.addColorStop(0.6, "rgba(200,150,10,0.8)");
        penGrad.addColorStop(1, "rgba(100,60,0,0.6)");
        ctx.fillStyle = penGrad;
        ctx.beginPath();
        ctx.roundRect(-penLen, -penW / 2, penLen, penW, 2);
        ctx.fill();

        ctx.fillStyle = "rgba(255,220,80,0.95)";
        ctx.beginPath();
        ctx.moveTo(0, -2);
        ctx.lineTo(6, 0);
        ctx.lineTo(0, 2);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }

      ctx.strokeStyle = "rgba(245,197,24,0.06)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(sheetX + padX, sheetY + padY * 2.5);
      ctx.lineTo(sheetX + sheetW - padX, sheetY + padY * 2.5);
      ctx.stroke();
      ctx.restore();

      pts.forEach((pt) => {
        pt.x += pt.vx;
        pt.y += pt.vy;
        if (pt.x < 0) pt.x = 1;
        if (pt.x > 1) pt.x = 0;
        if (pt.y < 0) pt.y = 1;
        if (pt.y > 1) pt.y = 0;
        const px = pt.x * w;
        const py = pt.y * h;
        const dist = Math.sqrt((px - mx * w) ** 2 + (py - my * h) ** 2);
        const bright = Math.max(0, 1 - dist / (w * 0.28));
        const pa =
          (0.12 + bright * 0.55) * (0.6 + Math.sin(t * 0.5 + pt.phase) * 0.4);
        if (pa < 0.02) return;
        ctx.fillStyle = `rgba(245,197,24,${pa})`;
        ctx.beginPath();
        ctx.arc(px, py, safeR(pt.size + bright * 2), 0, Math.PI * 2);
        ctx.fill();
      });

      t += 0.016;
      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, [mouseRef]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
      }}
    />
  );
};

// ── 2. STATS TICKER ───────────────────────────────────────────────────────────
const STATS = [
  { n: "24+", label: "Template", sub: "Un design per ogni settore" },
  { n: "€2,99", label: "Pagamento unico", sub: "Nessun abbonamento mai" },
  { n: "A4", label: "Export PDF", sub: "Pronto per l'invio immediato" },
  { n: "100%", label: "Privacy", sub: "Dati solo nel tuo browser" },
];

const StatsTicker = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setActive((a) => (a + 1) % STATS.length),
      2600,
    );
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ position: "relative" }}>
      <div
        className="cvcraft-stats-row"
        style={{ display: "flex", gap: 0, alignItems: "stretch" }}
      >
        {STATS.map((s, i) => (
          <div
            key={i}
            className={`cvcraft-stats-item ${i === active ? "is-active" : ""}`}
            style={{
              flex: 1,
              padding: "16px 20px",
              borderLeft: "1px solid rgba(255,255,255,0.06)",
              transition: "all .5s ease",
              opacity: i === active ? 1 : 0.28,
              transform: i === active ? "translateY(0)" : "translateY(5px)",
            }}
          >
            <div
              style={{
                fontFamily: "'Syne',sans-serif",
                fontWeight: 900,
                fontSize: 28,
                color: i === active ? C.gold : C.text,
                letterSpacing: "-0.04em",
                lineHeight: 1,
                transition: "color .5s",
              }}
            >
              {s.n}
            </div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: C.text,
                marginTop: 5,
              }}
            >
              {s.label}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {s.sub}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          position: "absolute",
          bottom: -10,
          left: 0,
          right: 0,
          height: 1,
          background: "rgba(255,255,255,0.05)",
        }}
      />
      <div
        className="cvcraft-stats-bar"
        style={{
          position: "absolute",
          bottom: -10,
          left: `${(active / STATS.length) * 100}%`,
          width: `${100 / STATS.length}%`,
          height: 2,
          background: C.gold,
          borderRadius: 2,
          boxShadow: `0 0 12px ${C.gold}`,
          transition: "left .6s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      />
    </div>
  );
};

// ── 3. SECTORS STRIP ───────────────────────────────────────────────────────────
const SECTORS = [
  "Tech / Dev",
  "Creativo",
  "Marketing",
  "Sanità",
  "Accademia",
  "Ingegneria",
  "Luxury",
  "Media",
  "Consulting",
  "Management",
  "Finance",
  "Neo-laureati",
  "Architettura",
  "Ristorazione",
];

const LogoStrip = () => (
  <div
    style={{
      overflow: "hidden",
      position: "relative",
      padding: "14px 0",
      borderTop: "1px solid rgba(255,255,255,0.05)",
    }}
  >
    <style
      dangerouslySetInnerHTML={{
        __html: `@keyframes stripL{from{transform:translateX(0)}to{transform:translateX(-50%)}}`,
      }}
    />
    <div
      style={{
        display: "flex",
        gap: 32,
        width: "max-content",
        animation: "stripL 20s linear infinite",
      }}
    >
      {[...SECTORS, ...SECTORS].map((s, i) => (
        <div
          key={i}
          style={{
            fontSize: 11,
            fontFamily: "'DM Mono',monospace",
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.22)",
            whiteSpace: "nowrap",
            display: "flex",
            alignItems: "center",
            gap: 16,
          }}
        >
          {s}
          <span style={{ color: C.goldBorder, fontSize: 7 }}>✦</span>
        </div>
      ))}
    </div>
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          "linear-gradient(to right,#080909 0%,transparent 8%,transparent 92%,#080909 100%)",
        pointerEvents: "none",
      }}
    />
  </div>
);

// ── 4. MAGNETIC WORD ───────────────────────────────────────────────────────────
const MW = ({ children, color }) => {
  const ref = useRef();
  const [off, setOff] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const dx = ((e.clientX - (r.left + r.width / 2)) / r.width) * 0.7;
    const dy = ((e.clientY - (r.top + r.height / 2)) / r.height) * 0.7;
    if (Math.abs(dx) < 0.8 && Math.abs(dy) < 0.8)
      setOff({ x: dx * 20, y: dy * 12 });
    else setOff({ x: 0, y: 0 });
  };
  return (
    <span
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => setOff({ x: 0, y: 0 })}
      style={{
        display: "inline-block",
        transform: `translate(${off.x}px,${off.y}px)`,
        transition: "transform .28s cubic-bezier(0.34,1.56,0.64,1)",
        color: color || "inherit",
        cursor: "default",
      }}
    >
      {children}
    </span>
  );
};

// ── 5. MAIN LANDING COMPONENT ──────────────────────────────────────────────────
const Landing = ({ onStart }) => {
  const mouseRef = useRef({ x: 0.5, y: 0.5 });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const h = (e) => {
      mouseRef.current = {
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      };
    };
    window.addEventListener("mousemove", h);
    setTimeout(() => setMounted(true), 80);
    return () => window.removeEventListener("mousemove", h);
  }, []);

  return (
    <>
      <div
        style={{
          position: "relative",
          minHeight: "100vh",
          overflow: "hidden",
          background: "#080909",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CVSheetCanvas mouseRef={mouseRef} />

        <div
          className="cvcraft-vignette"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "radial-gradient(ellipse at 35% 50%, transparent 30%, rgba(8,9,9,0.6) 100%)",
          }}
        />

        <style
          dangerouslySetInnerHTML={{
            __html: `
          @keyframes badgePulse{0%,100%{box-shadow:0 0 0 0 rgba(245,197,24,0.35)}50%{box-shadow:0 0 0 10px rgba(245,197,24,0)}}
          @keyframes ctaBounce{0%,100%{transform:translateY(0) scale(1)}50%{transform:translateY(-5px) scale(1.02)}}
          @keyframes dotBlink{0%,100%{opacity:1}50%{opacity:.4}}
          
          @media (max-width: 768px) {
            .cvcraft-vignette { background: radial-gradient(circle at center, transparent 20%, rgba(8,9,9,0.85) 100%) !important; }
            .cvcraft-hero-container { padding: 52px 24px 32px !important; align-items: center !important; text-align: center !important; justify-content: center !important; }
            .cvcraft-badge-container { margin-bottom: 20px !important; }
            .cvcraft-badge-container > div { padding: 6px 14px 6px 9px !important; }
            .cvcraft-headline { max-width: 100% !important; margin-bottom: 18px !important; }
            .cvcraft-subtext { max-width: 100% !important; font-size: 15px !important; line-height: 1.6 !important; margin-bottom: 32px !important; }
            .cvcraft-cta-wrapper { flex-direction: column !important; align-items: center !important; gap: 20px !important; width: 100% !important; margin-bottom: 40px !important; }
            .cvcraft-cta-wrapper button { width: 100% !important; max-width: 340px !important; justify-content: center !important; padding: 14px 28px !important; font-size: 15px !important; }
            .cvcraft-checklist { align-items: flex-start !important; text-align: left !important; }
            .cvcraft-stats-card { width: 100% !important; max-width: 100% !important; padding: 16px 12px 20px !important; }
            .cvcraft-stats-row { display: block !important; height: 72px !important; overflow: hidden !important; position: relative !important; }
            .cvcraft-stats-item { display: none !important; }
            .cvcraft-stats-item.is-active { display: flex !important; flex-direction: column !important; align-items: center !important; text-align: center !important; border-left: none !important; padding: 0 !important; position: absolute !important; inset: 0 !important; justify-content: center !important; }
            .cvcraft-stats-bar { left: 50% !important; width: 60px !important; transform: translateX(-50%) !important; transition: none !important; }
          }
        `,
          }}
        />

        <div
          className="cvcraft-hero-container"
          style={{
            position: "relative",
            zIndex: 2,
            flex: 1,
            maxWidth: 1200,
            margin: "0 auto",
            width: "100%",
            padding: "0 48px",
            display: "flex",
            flexDirection: "column",
            justifycontent: "center",
            paddingBottom: 32,
          }}
        >
          <div
            className="cvcraft-badge-container"
            style={{
              marginBottom: 28,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(20px)",
              transition: "all .7s ease",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "7px 18px 7px 9px",
                background: "rgba(245,197,24,0.07)",
                border: `1px solid ${C.goldBorder}`,
                borderRadius: 100,
                animation: "badgePulse 3s infinite",
              }}
            >
              <div
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: C.gold,
                  animation: "dotBlink 2s infinite",
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: C.gold,
                  fontFamily: "'DM Mono',monospace",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                }}
              >
                24 template · offerta lancio €2,99
              </span>
            </div>
          </div>

          <h1
            className="cvcraft-headline"
            style={{
              fontFamily: "'Syne',sans-serif",
              fontWeight: 900,
              fontSize: "clamp(44px,6.8vw,96px)",
              lineHeight: 0.94,
              letterSpacing: "-0.05em",
              margin: "0 0 28px",
              maxWidth: 660,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(30px)",
              transition: "all .8s .08s ease",
            }}
          >
            <span
              style={{
                display: "block",
                background:
                  "linear-gradient(135deg,#fff 35%,rgba(255,255,255,0.42))",
                WebkitBackgroundClip: "text",
                color: "#fff",
                WebkitTextFillColor: "unset",
              }}
            >
              <MW>Il CV</MW> <MW color={C.gold}>giusto</MW>
            </span>
            <span
              style={{
                display: "block",
                fontFamily: "'Instrument Serif',serif",
                fontStyle: "italic",
                background: `linear-gradient(135deg,${C.gold} 20%,#FF7A30)`,
                WebkitBackgroundClip: "text",
                color: "#fff",
                WebkitTextFillColor: "unset",
              }}
            >
              <MW>per ogni</MW> <MW>mestiere.</MW>
            </span>
          </h1>

          <p
            className="cvcraft-subtext"
            style={{
              color: "rgba(255,255,255,0.46)",
              fontSize: 17,
              maxWidth: 440,
              lineHeight: 1.76,
              marginBottom: 40,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(20px)",
              transition: "all .8s .16s ease",
            }}
          >
            24 template professionali, uno per ogni settore.
            <br />
            Compila, vedi l'anteprima, scarica.{" "}
            <span style={{ color: C.text, fontWeight: 600 }}>
              Paghi solo se sei soddisfatto.
            </span>
          </p>

          <div
            className="cvcraft-cta-wrapper"
            style={{
              display: "flex",
              gap: 20,
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: 52,
              opacity: mounted ? 1 : 0,
              transform: mounted ? "translateY(0)" : "translateY(16px)",
              transition: "all .8s .24s ease",
            }}
          >
            <button
              onClick={onStart}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                padding: "15px 36px",
                background: `linear-gradient(135deg,${C.gold},#D4840A)`,
                border: "none",
                borderRadius: 14,
                fontFamily: "'Syne',sans-serif",
                fontWeight: 900,
                fontSize: 16,
                color: "#0A0600",
                cursor: "pointer",
                boxShadow: "0 8px 44px rgba(245,197,24,0.3)",
                animation: "ctaBounce 3.5s ease infinite",
                letterSpacing: "-0.01em",
              }}
            >
              <span style={{ fontSize: 18 }}>✦</span>
              Scegli il tuo template
              <span style={{ fontSize: 18 }}>→</span>
            </button>

            <div
              className="cvcraft-checklist"
              style={{ display: "flex", flexDirection: "column", gap: 6 }}
            >
              {[
                "Gratis per creare",
                "Anteprima prima di pagare",
                "€2,99 una tantum, nessun abbonamento",
              ].map((l, i) => (
                <div
                  key={i}
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ color: C.success, fontSize: 13 }}>✓</span>
                  {l}
                </div>
              ))}
            </div>
          </div>

          <div
            className="cvcraft-stats-card"
            style={{
              opacity: mounted ? 1 : 0,
              transition: "all .8s .32s ease",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16,
              padding: "20px 4px 28px",
              backdropFilter: "blur(12px)",
              maxWidth: 640,
            }}
          >
            <div
              style={{
                fontSize: 9.5,
                color: C.muted,
                fontFamily: "'DM Mono',monospace",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                textAlign: "center",
                marginBottom: 16,
              }}
            >
              Perché scegliere CVcraft
            </div>
            <StatsTicker />
          </div>
        </div>

        <div style={{ position: "relative", zIndex: 2 }}>
          <LogoStrip />
        </div>
      </div>
    </>
  );
};

export default Landing;
